export enum UserInteractionStatusEnum {
    UNREAD = 'UNREAD',
    READ = 'READ',
    ARCHIVED = 'ARCHIVED',
}

export type UserFullName = {
    userSub: string;
    givenName: string;
    familyName: string;
};

export type Notification = {
    id: string;
    title: string;
    message: Record<string, any> | null;
    createdAt: Date;
    initiatorUser: UserFullName;
    initiatorUserSub: string;
    recipients: {
        createdAt: Date;
        recipient: UserFullName;
    }[];
    type: string;
    userInteractionStatus: UserInteractionStatusEnum;
};

export type NotificationResponseDto = {
    items: Notification[];
    dateFrom?: string;
    dateTo?: string;
    total: number;
};

export type NotificationSettingsDto = {
    id: string;
    userSub: string;
    name: string;
    enabled: boolean;
    markedAsObsolete: boolean;
};

export enum NotificationTitleEnum {
    LEAVE_REQUEST_APPROVE = 'LEAVE_REQUEST_APPROVE',
    LEAVE_REQUEST_REJECT = 'LEAVE_REQUEST_REJECT',
    LEAVE_REQUEST_DELETION = 'LEAVE_REQUEST_DELETE',
    LEAVE_REQUEST_UPDATE_ONGOING = 'LEAVE_REQUEST_UPDATE_ONGOING',
    POLICY_DELETION = 'POLICY_DELETION',
    POLICY_MEMBERS_CHANGED = 'POLICY_MEMBERS_CHANGED',
    POLICY_CHANGE = 'POLICY_CHANGE',
    ROLE_CHANGE = 'ROLE_CHANGE',
    BRANCH_CHANGE = 'BRANCH_CHANGE',
    DOCUMENT_UPLOAD = 'DOCUMENT_UPLOAD',
    TEAM_MEMBERS_ADD = 'TEAM_MEMBERS_ADD',
    TEAM_LEAD_UPDATE = 'TEAM_LEAD_UPDATE',
    CHANGE_TEAM_LEAD = 'CHANGE_TEAM_LEAD',
    TEAM_MEMBER_REMOVE = 'TEAM_MEMBER_REMOVE',
    REMOVE_ASSIGNEE = 'REMOVE_ASSIGNEE',
    ADD_ASSIGNEE = 'ADD_ASSIGNEE',
    SUBMISSION_INTERVAL = 'SUBMISSION_INTERVAL',
    LEAVE_REQUEST_CREATION = 'LEAVE_REQUEST_CREATION',
    LOYALTY_PROGRAM_BONUS = 'LOYALTY_PROGRAM_BONUS',
    POLICY_NAME_UPDATED = 'POLICY_NAME_UPDATED',
    MEDICAL_LEAVE_UPDATE = 'MEDICAL_LEAVE_UPDATE',
    MEDICAL_LEAVE_REQUEST_CREATION = 'MEDICAL_LEAVE_REQUEST_CREATION',
    MEDICAL_LEAVE_REQUEST_APPROVE_UPDATE = 'MEDICAL_LEAVE_REQUEST_APPROVE_UPDATE',
    MEDICAL_LEAVE_REQUEST_REJECT_UPDATE = 'MEDICAL_LEAVE_REQUEST_REJECT_UPDATE',
    MEDICAL_LEAVE_REQUEST_ENDED = 'MEDICAL_LEAVE_REQUEST_ENDED',
    MEDICAL_LEAVE_REQUEST_UPDATED = 'MEDICAL_LEAVE_REQUEST_UPDATED',
    EVALUATION_UPDATE_REQUESTED = 'EVALUATION_UPDATE_REQUESTED',
    APPROVE_REJECT_UPDATE = 'APPROVE_REJECT_UPDATE',
    INVOICE_UPDATED = 'INVOICE_UPDATED',
    REQUESTED_EVALUATION = 'REQUESTED_EVALUATION',
    PERFORMED_EVALUATION = 'PERFORMED_EVALUATION',
    CREATE_NOTE_ACTIVITY = 'CREATE_NOTE_ACTIVITY',
    CREATE_MEET_ACTIVITY = 'CREATE_MEET_ACTIVITY',
}

export type NotificationGeneric<TMessage> = {
    id: string;
    notificationTitle: NotificationTitleEnum;
    message: TMessage;
    createdAt: Date;
    initiatorUserSub: string;
    initiators: UserFullName;
    recipients: {
        createdAt: Date;
        recipient: UserFullName;
    }[];
};

const adminNotificationSettings: Record<string, string[]> = {
    'Leave Request': [
        'LEAVE_REQUEST_APPROVED',
        'LEAVE_REQUEST_REJECTED',
        'LEAVE_REQUEST_CREATED',
        'LEAVE_REQUEST_WITHDRAWN',
        'LEAVE_REQUEST_UPDATED',
    ],
    'Medical Leave': ['MEDICAL_LEAVE_UPDATED'],
    'Medical Leave Request': ['MEDICAL_LEAVE_REQUEST_CREATION', 'MEDICAL_LEAVE_REQUEST_APPROVE_UPDATE', 'MEDICAL_LEAVE_REQUEST_REJECT_UPDATE'],
    Branch: ['BRANCH_CHANGE'],
    Timesheet: ['TIMELOG_SUBMISSION'],
    Team: ['TEAM_MEMBER_ADDED', 'TEAM_MEMBER_REMOVED', 'TEAM_ADDED', 'TEAM_ROLE_UPDATED'],
    Documents: ['DOCUMENT_UPLOADED'],
    Role: ['ROLE_CHANGED'],
    Policy: ['POLICY_ASSIGNED', 'POLICY_UNASSIGNED', 'POLICY_AMOUNT_CHANGED'],
    Project: ['PROJECT_ASSIGNEE_ADDED', 'PROJECT_ASSIGNEE_REMOVED'],
};

const memberNotificationSettings = {
    'Leave Request': ['LEAVE_REQUEST_APPROVED', 'LEAVE_REQUEST_REJECTED'],
    'Medical Leave': ['MEDICAL_LEAVE_UPDATED'],
    'Medical Leave Request': ['MEDICAL_LEAVE_REQUEST_CREATION', 'MEDICAL_LEAVE_REQUEST_APPROVE_UPDATE', 'MEDICAL_LEAVE_REQUEST_REJECT_UPDATE'],
    Branch: ['BRANCH_CHANGE'],
    Documents: ['DOCUMENT_UPLOADED'],
    Role: ['ROLE_CHANGED'],
    Team: ['TEAM_MEMBER_ADDED', 'TEAM_MEMBER_REMOVED'],
    Policy: ['POLICY_ASSIGNED', 'POLICY_UNASSIGNED', 'POLICY_AMOUNT_CHANGED'],
    Project: ['PROJECT_ASSIGNEE_ADDED', 'PROJECT_ASSIGNEE_REMOVED'],
};

const teamLeadNotificationSettings = {
    'Leave Request': ['LEAVE_REQUEST_APPROVED', 'LEAVE_REQUEST_REJECTED'],
    'Medical Leave': ['MEDICAL_LEAVE_UPDATED'],
    Branch: ['BRANCH_CHANGE'],
    Documents: ['DOCUMENT_UPLOADED'],
    Role: ['ROLE_CHANGED'],
    Team: ['TEAM_MEMBER_ADDED', 'TEAM_MEMBER_REMOVED', 'TEAM_ROLE_UPDATED', 'TEAM_ADDED'],
    Policy: ['POLICY_ASSIGNED', 'POLICY_UNASSIGNED', 'POLICY_AMOUNT_CHANGED'],
    Project: ['PROJECT_ASSIGNEE_ADDED', 'PROJECT_ASSIGNEE_REMOVED'],
};

export const roleBasedNotifications: Record<string, Record<string, string[]>> = {
    ADMIN: { ...adminNotificationSettings },
    OWNER: { ...adminNotificationSettings },
    ORDINARY: { ...memberNotificationSettings },
    TEAM_LEAD: { ...teamLeadNotificationSettings },
    PM: { ...teamLeadNotificationSettings },
};
